import { useTranslator } from "@eo-locale/react";

import Cookies from "js-cookie";

import {
  IconButton,
  Button,
  Box,
  FiMessageCircle,
  useBreakpointValue
} from "@/storybook-components";
import { useAppSelector, useAppDispatch } from "@/redux/store/hooks";

import useUnverifiedModal from "@/hooks/useUnverfiedModal";
import useAuthGate from "@/hooks/useAuthGate";
import {
  setTriggerPoint,
  setUnverifiedModalOpen,
  showUnverifiedDisabledModal
} from "@/redux/features/unverified-modal/slice";
import { setTriggerPointCookie } from "@/helpers/set-trigger-point-cookie";

interface AddCommentButtonProps {
  onClick: () => void;
  disabled: boolean;
  "data-testid"?: string;
}

const AddCommentButton = (props: AddCommentButtonProps) => {
  const { onClick, disabled, "data-testid": testId } = props;
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();

  const isMobileScreen = useBreakpointValue({
    base: true,
    lg: false
  });

  const { brainstormer, idea } = useAppSelector((state) => state.brainstormers);

  const projectIdFromStore = useAppSelector(
    (state) => state.project.project?.id
  );

  const { triggerPoint } = useAppSelector((state) => state.unverifiedModal);
  const cookieValues = Cookies.get("trigger_point");
  let cookieTriggerPoint: { triggeredSource?: string } | undefined;
  if (cookieValues) {
    try {
      cookieTriggerPoint = JSON.parse(cookieValues) as {
        triggeredSource?: string;
      };
    } catch {
      cookieTriggerPoint = undefined;
    }
  }

  const { allowUnverifiedParticipation, layout } = brainstormer;

  const {
    isRegisteredUserLoggedIn,
    isUnverfiedUserLoggedIn,
    isGuestUser,
    isGuestParticipationEnabled,
    requireProfileCompletion,
    openGuestLoginModal
  } = useAuthGate();

  const guestParticipationAllowed =
    isGuestParticipationEnabled && allowUnverifiedParticipation;
  const unverifiedParticipationAllowed =
    !isGuestParticipationEnabled && allowUnverifiedParticipation;
  const toolAllowsGuestOrUnverifiedParticipationFlow =
    guestParticipationAllowed || unverifiedParticipationAllowed;

  useUnverifiedModal(allowUnverifiedParticipation, (isProceedAllowed) => {
    if (isProceedAllowed) {
      dispatch(setTriggerPoint({ isTriggeredPointActive: false }));
      if (
        triggerPoint.triggeredSource === "comment" ||
        cookieTriggerPoint?.triggeredSource === "comment"
      ) {
        onClick();
      }

      Cookies.remove("trigger_point");
    }
  });

  const handleAddComment = () => {
    if (disabled) return;

    if (isGuestUser) {
      if (!toolAllowsGuestOrUnverifiedParticipationFlow) {
        if (isGuestParticipationEnabled) {
          const gateTrigger = {
            triggeredSource: "comment",
            isTriggeredPointActive: true,
            id: idea?.id,
            pendingProfileGate: true
          };
          Cookies.set("trigger_point", JSON.stringify(gateTrigger), {
            expires: 1,
            secure: true,
            sameSite: "Strict"
          });
          requireProfileCompletion({
            reason: "profileGate.requiredForContribution",
            source: "comment"
          });
        } else {
          onClick();
        }
        return;
      }

      onClick();
      return;
    }

    const shouldHandleAddComment =
      !isRegisteredUserLoggedIn &&
      (toolAllowsGuestOrUnverifiedParticipationFlow ||
        !isUnverfiedUserLoggedIn);

    if (shouldHandleAddComment) {
      const commentTriggerPayload = {
        triggeredSource: "comment" as const,
        id: idea?.id != null ? String(idea.id) : undefined,
        toolId: brainstormer?.id != null ? String(brainstormer.id) : undefined,
        projectId:
          projectIdFromStore != null ? String(projectIdFromStore) : undefined
      };

      if (
        toolAllowsGuestOrUnverifiedParticipationFlow &&
        isGuestParticipationEnabled
      ) {
        const inactiveCommentTrigger = {
          ...commentTriggerPayload,
          isTriggeredPointActive: false
        };
        setTriggerPointCookie(inactiveCommentTrigger);
        dispatch(setTriggerPoint(inactiveCommentTrigger));
        openGuestLoginModal({ comment: true });
        return;
      }

      if (
        layout === "BudgetAllocator" &&
        toolAllowsGuestOrUnverifiedParticipationFlow &&
        !isGuestParticipationEnabled
      ) {
        const pausedCommentTrigger = {
          ...commentTriggerPayload,
          isTriggeredPointActive: false
        };
        setTriggerPointCookie(pausedCommentTrigger);
        dispatch(setTriggerPoint(pausedCommentTrigger));
        dispatch(setUnverifiedModalOpen(false));
        dispatch(showUnverifiedDisabledModal(true));
        return;
      }

      const activeCommentTrigger = {
        ...commentTriggerPayload,
        isTriggeredPointActive: true
      };
      setTriggerPointCookie(activeCommentTrigger);
      dispatch(setTriggerPoint(activeCommentTrigger));
      return;
    }

    onClick();
  };

  return (
    <Box
      position="fixed"
      bottom={5}
      right={4}
      zIndex={1}
      data-testid={testId ?? "ideas-add-comment-button"}
    >
      {isMobileScreen ? (
        <IconButton
          aria-label={translate("brainstormers.addComment")}
          icon={<FiMessageCircle />}
          size="lg"
          variant="primary"
          borderRadius="50%"
          boxShadow="lg"
          onClick={handleAddComment}
          disabled={disabled}
          data-testid="ideas-add-comment-icon-button"
        />
      ) : (
        <Button
          aria-label={translate("brainstormers.addComment")}
          variant="primary"
          size={{ base: "lg", lg: "md" }}
          leftIcon={<FiMessageCircle />}
          fontWeight="semibold"
          fontSize="md"
          py={2}
          px={4}
          onClick={handleAddComment}
          hidden={isMobileScreen}
          disabled={disabled}
          data-testid="ideas-add-comment-text-button"
        >
          {translate("brainstormers.addComment")}
        </Button>
      )}
    </Box>
  );
};

export default AddCommentButton;
